/**
 * Parse a persisted invitation value into a Quill Delta or plain text.
 *
 * Three wire shapes exist for the same field:
 *  - the web editor writes the wrapped form `{"ops":[{insert:…}]}`;
 *  - the mobile app writes the BARE ops array `[{insert:…}]`;
 *  - older invitations are plain text (never JSON).
 *
 * A bare array is only treated as a Delta when it actually looks like Delta
 * content ops (a non-empty array of `{insert:…}` objects). This matters
 * because a legacy plain-text invitation can itself be valid JSON — e.g. a
 * user typed `[59.3, 18.0]` or `["deer","boar"]` — and such values must fall
 * through to plain text rather than be fed to Quill (which would drop the
 * non-op entries, render empty, or throw). Shared by DeltaRenderer (read-only)
 * and DeltaEditor so the two never diverge on which shapes they accept.
 */
export type ParsedDelta =
  | { kind: 'delta'; ops: ReadonlyArray<unknown> }
  | { kind: 'plain'; text: string }
  | { kind: 'empty' }

/** A non-empty array whose every element is an object carrying an `insert`. */
function isDeltaContentOps(value: unknown): value is ReadonlyArray<unknown> {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (op) => op !== null && typeof op === 'object' && 'insert' in op,
    )
  )
}

export function parseDeltaValue(raw: string | null): ParsedDelta {
  if (!raw) return { kind: 'empty' }
  try {
    const parsed = JSON.parse(raw) as unknown
    // Wrapped form from the web editor: an object with an `ops` array.
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      'ops' in parsed &&
      Array.isArray((parsed as { ops: unknown }).ops)
    ) {
      return { kind: 'delta', ops: (parsed as { ops: unknown[] }).ops }
    }
    // Bare ops array from mobile — only when it truly looks like content ops.
    if (isDeltaContentOps(parsed)) {
      return { kind: 'delta', ops: parsed }
    }
  } catch {
    /* not JSON — fall through to plain text */
  }
  return { kind: 'plain', text: raw }
}

/**
 * The formats an invitation may carry. Quill keeps everything a paste brings
 * unless told otherwise, and a paste from Word brings fonts, sizes, colours,
 * highlight and alignment. The app renders the same document with
 * flutter_quill, which throws on any colour it cannot parse (Word's
 * `windowtext`, for one), so an invitation pasted from a document could not be
 * opened in the app at all.
 *
 * The list is what EITHER editor can write, not only this toolbar: the app's
 * editor also offers strikethrough, headings, font size and checklists, and a
 * narrower list here would strip those from an invitation the moment someone
 * edited it on web. `indent` is how both editors nest a list item. The app
 * drops everything outside this same set when it renders, so an invitation
 * saved before this list existed shows the same on both platforms.
 */
export const INVITATION_FORMATS: readonly string[] = [
  'bold',
  'italic',
  'underline',
  'strike',
  'link',
  'header',
  'size',
  'list',
  'indent',
  'image',
]
